import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, CreditCard, Wallet, Building2, Store, CheckCircle2, Plus, AlertTriangle, Truck, StickyNote } from "lucide-react";
import * as ella from "@/lib/ellaApi";
import { useSeo } from "@/lib/seo";
import { fmtIDR, paymentImg, effectivePrice } from "@/lib/ellaApi";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import AddressFormModal from "@/components/AddressFormModal";

const CATEGORY_ICONS = {
  bank_transfer: Building2,
  cstore: Store,
  echannel: CreditCard,
  gopay: Wallet,
};

const CATEGORY_LABELS = {
  bank_transfer: "Transfer Bank (Virtual Account)",
  cstore: "Gerai Retail",
  echannel: "Mandiri Bill Payment",
  gopay: "E-Wallet",
  direct: "Pembayaran Langsung",
};

export default function CheckoutPage() {
  const nav = useNavigate();
  const { isAuthed } = useAuth();
  const { cartItems, cartByBranch, cartSubtotal, cartHydrated, paymentMethods, refreshCart, updateCartQty } = useStore();
  const [addresses, setAddresses] = useState([]);
  const [delivery, setDelivery] = useState(null); // [{ Branch, Fee, IsFound }]
  const [deliveryLoading, setDeliveryLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addrModalOpen, setAddrModalOpen] = useState(false);

  useSeo({ title: "Checkout" });

  const loadDelivery = useCallback(async () => {
    setDeliveryLoading(true);
    try {
      const res = await ella.calculateDelivery();
      setDelivery(res.status ? res.data || [] : []);
    } catch { setDelivery([]); }
    finally { setDeliveryLoading(false); }
  }, []);

  const refreshAddresses = useCallback(async () => {
    try {
      const res = await ella.getAddresses();
      if (res.status) setAddresses(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (!isAuthed) { nav("/cart"); return; }
    if (!cartHydrated) return;            // wait for cart to hydrate
    if (!cartItems.length) { nav("/cart"); return; }
    refreshAddresses();
    loadDelivery();
  }, [isAuthed, cartHydrated, cartItems.length]); // eslint-disable-line

  const primaryAddr = addresses.find((a) => Number(a.IsDefault) === 1) || addresses[0];

  // Delivery is billed per branch present in the cart; every branch must have a
  // route (IsFound) to the customer's district, otherwise checkout is blocked —
  // same rule as the legacy app (it zeroed hdnTotalPayment when ANY branch had
  // IsFound=0). Blocking matters: doPay would charge the full cart but only
  // create orders for routable branches, silently dropping the rest.
  const deliveryRows = delivery || [];
  const shipping = deliveryRows.reduce((s, r) => s + Number(r.Fee || 0), 0);
  const deliverable = deliveryRows.length > 0 && deliveryRows.every((r) => Number(r.IsFound) === 1);
  const blockedBranches = deliveryRows.filter((r) => Number(r.IsFound) !== 1).map((r) => r.Branch);
  const total = cartSubtotal + shipping;

  // One-tap unblock: drop the cart items that belong to unroutable branches.
  const [removingBlocked, setRemovingBlocked] = useState(false);
  const removeBlockedItems = async () => {
    setRemovingBlocked(true);
    try {
      const doomed = cartItems.filter((i) => blockedBranches.includes(i.Branch));
      for (const item of doomed) {
        await updateCartQty(item.ProductID, 0, item.Notes || "");
      }
      toast.success(`Item dari cabang ${blockedBranches.join(", ")} dihapus dari keranjang`);
      await loadDelivery();
    } finally { setRemovingBlocked(false); }
  };

  const groupedMethods = paymentMethods.reduce((acc, m) => {
    (acc[m.Category] ||= []).push(m);
    return acc;
  }, {});

  const handlePay = async () => {
    if (!primaryAddr) { toast.error("Pilih alamat pengiriman dulu"); return; }
    if (!deliverable) { toast.error("Maaf, tidak bisa menghitung biaya kirim ke alamat kamu, silahkan kontak admin"); return; }
    if (!paymentMethod) { toast.error("Silahkan pilih Metode Pembayaran terlebih dahulu"); return; }
    setSubmitting(true);
    try {
      // doPay expects the bare code before "|" (goes straight to Midtrans)
      const methodCode = paymentMethod.split("|")[0];
      const res = await ella.doPay(methodCode);
      if (res.status) {
        toast.success("Pesanan berhasil dibuat!");
        // Navigate BEFORE refreshing: doPay empties the server cart, and the
        // empty-cart redirect effect would otherwise race us to /cart.
        nav("/order/success", { state: { payment: res.data }, replace: true });
        refreshCart();
      } else {
        toast.error(res.message || "Gagal memproses pembayaran");
      }
    } catch (e) {
      toast.error("Gagal memproses pembayaran");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pb-32">
      <div className="px-4 py-3 flex items-center gap-3 sticky top-[60px] bg-white z-30 border-b border-gray-100">
        <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-display text-lg font-bold text-gray-900">Checkout</h1>
      </div>

      <div className="p-4 space-y-4">
        {/* Address */}
        <section data-testid="checkout-address-section">
          <h2 className="font-display font-bold text-sm text-gray-900 mb-2 flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-[#D32F2F]" /> Alamat Pengiriman
          </h2>
          {primaryAddr ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-3" data-testid="checkout-selected-address">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900">{primaryAddr.Name} • {primaryAddr.Phone}</p>
                  <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                    {primaryAddr.Address}, {primaryAddr.DistrictName}, {primaryAddr.CityName}, {primaryAddr.StateName} {primaryAddr.PostalCode}
                  </p>
                </div>
                <button onClick={() => nav("/account/addresses?from=checkout")} className="text-xs text-[#D32F2F] font-semibold ml-2 shrink-0" data-testid="checkout-change-address">Ubah</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddrModalOpen(true)} className="w-full bg-white border-2 border-dashed border-gray-200 rounded-2xl p-4 text-center hover:border-[#D32F2F]" data-testid="checkout-add-address">
              <Plus className="w-5 h-5 mx-auto text-gray-400 mb-1" />
              <span className="text-sm text-gray-600">Tambah Alamat Pengiriman</span>
            </button>
          )}
          {primaryAddr && !deliveryLoading && !deliverable && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800" data-testid="checkout-undeliverable-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  {blockedBranches.length
                    ? <>Ongkir dari cabang <b>{blockedBranches.join(", ")}</b> ke alamat ini belum tersedia, jadi pembayaran belum bisa diproses.</>
                    : <>Maaf, tidak bisa menghitung biaya kirim ke alamat kamu.</>}
                  {" "}Silahkan pilih alamat lain, hapus item cabang tersebut, atau kontak admin.
                </span>
              </div>
              {blockedBranches.length > 0 && (
                <div className="flex gap-2 mt-2.5">
                  <button
                    onClick={removeBlockedItems}
                    disabled={removingBlocked}
                    className="flex-1 h-9 rounded-full bg-amber-600 text-white font-semibold text-xs disabled:opacity-60"
                    data-testid="checkout-remove-blocked-items"
                  >
                    {removingBlocked ? "Menghapus..." : `Hapus item ${blockedBranches.join(", ")}`}
                  </button>
                  <button
                    onClick={() => nav("/account/addresses?from=checkout")}
                    className="flex-1 h-9 rounded-full border border-amber-600 text-amber-700 font-semibold text-xs"
                    data-testid="checkout-change-address-warning"
                  >
                    Ubah Alamat
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Order summary — grouped per branch, each with its own ongkir status */}
        <section data-testid="checkout-items-section">
          <h2 className="font-display font-bold text-sm text-gray-900 mb-2">Ringkasan Pesanan</h2>
          <div className="space-y-3">
            {cartByBranch.map((group) => {
              const feeRow = deliveryRows.find((r) => r.Branch === group.branch);
              const blocked = !deliveryLoading && (!feeRow || Number(feeRow.IsFound) !== 1);
              return (
                <div key={group.branch} className={`bg-white rounded-2xl border p-3 ${blocked ? "border-amber-300" : "border-gray-100"}`} data-testid={`checkout-branch-${group.branch}`}>
                  <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
                    <span className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                      <Store className="w-3.5 h-3.5 text-[#D32F2F]" /> Cabang {group.branch}
                    </span>
                    {blocked && (
                      <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid={`checkout-branch-blocked-${group.branch}`}>
                        ONGKIR BELUM TERSEDIA
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {group.items.map((i) => (
                      <div key={i.ProductID}>
                        <div className="flex justify-between gap-2 text-sm">
                          <span className="text-gray-700 line-clamp-1 flex-1">{i.Product} <span className="text-gray-400">× {i.Qty}</span></span>
                          <span className="font-semibold text-gray-900 shrink-0">
                            {fmtIDR(effectivePrice(i.Price, i.DiscountType, i.Discount) * Number(i.Qty || 0))}
                          </span>
                        </div>
                        {i.Notes && (
                          <p className="text-[11px] text-gray-500 mt-0.5 flex items-start gap-1" data-testid={`checkout-note-${i.ProductID}`}>
                            <StickyNote className="w-3 h-3 shrink-0 mt-0.5" />
                            <span className="italic">{i.Notes}</span>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-dashed border-gray-100 space-y-1 text-xs">
                    <div className="flex justify-between text-gray-500">
                      <span>Subtotal cabang</span>
                      <span>{fmtIDR(group.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="flex items-center gap-1 text-gray-500"><Truck className="w-3 h-3" /> Ongkir {group.branch}</span>
                      <span className={blocked ? "text-red-500 font-semibold" : "text-gray-700 font-medium"} data-testid={`checkout-branch-fee-${group.branch}`}>
                        {deliveryLoading ? "..." : blocked ? "—" : Number(feeRow.Fee) === 0 ? "Bebas Ongkir" : fmtIDR(feeRow.Fee)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {!deliverable && !deliveryLoading && (
            <p className="text-[11px] text-gray-400 mt-2 text-center" data-testid="checkout-payment-hidden-hint">
              Metode pembayaran akan muncul setelah ongkir bisa dihitung.
            </p>
          )}
        </section>

        {/* Payment method — hidden until delivery can be calculated, so the user
            resolves the ongkir issue before picking how to pay */}
        {deliverable && (
        <section data-testid="checkout-payment-section">
          <h2 className="font-display font-bold text-sm text-gray-900 mb-2 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4 text-[#D32F2F]" /> Metode Pembayaran
          </h2>
          <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
            {Object.entries(groupedMethods).map(([cat, methods]) => {
              const Icon = CATEGORY_ICONS[cat] || CreditCard;
              return (
                <div key={cat} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 flex items-center gap-2 text-xs font-semibold text-gray-700 border-b border-gray-100">
                    <Icon className="w-3.5 h-3.5" /> {CATEGORY_LABELS[cat] || cat}
                  </div>
                  <div className="divide-y divide-gray-100">
                    {methods.map((m) => {
                      const [code, label] = m.ID.split("|");
                      return (
                        <Label key={m.ID} htmlFor={`pm-${m.ID}`} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50" data-testid={`payment-${code}`}>
                          <RadioGroupItem id={`pm-${m.ID}`} value={m.ID} />
                          <img
                            src={paymentImg(m.ImagePath)}
                            alt={label}
                            className="w-10 h-7 object-contain"
                            onError={(e)=>{ e.target.style.display = "none"; }}
                          />
                          <span className="text-sm font-medium text-gray-900">{label || code}</span>
                          {paymentMethod === m.ID && <CheckCircle2 className="w-4 h-4 text-[#D32F2F] ml-auto" />}
                        </Label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </RadioGroup>
        </section>
        )}

        {/* Costs */}
        <section className="bg-white rounded-2xl border border-gray-100 p-3 space-y-1.5">
          <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span data-testid="checkout-subtotal">{fmtIDR(cartSubtotal)}</span></div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Ongkos kirim</span>
            <span data-testid="checkout-shipping">
              {deliveryLoading ? <span className="text-xs text-gray-400">Menghitung...</span>
                : deliverable ? fmtIDR(shipping)
                : <span className="text-xs text-red-500">Belum bisa dihitung</span>}
            </span>
          </div>
          <div className="border-t border-gray-100 pt-2 flex justify-between">
            <span className="font-display font-bold">Total</span>
            <span className="font-display font-bold text-lg text-[#D32F2F]" data-testid="checkout-total">{fmtIDR(total)}</span>
          </div>
        </section>
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 px-4 py-3 z-40">
        <Button
          onClick={handlePay}
          disabled={submitting || !primaryAddr || !paymentMethod || deliveryLoading || !deliverable}
          className="w-full h-12 btn-primary"
          data-testid="checkout-pay-button"
        >
          {submitting ? "Memproses..." : `Bayar Sekarang • ${fmtIDR(total)}`}
        </Button>
      </div>

      <AddressFormModal
        open={addrModalOpen}
        onClose={() => setAddrModalOpen(false)}
        onSaved={async () => { await refreshAddresses(); await loadDelivery(); }}
      />
    </div>
  );
}
